/**
 * Schedule run status — the wire contract between the schedule runner
 * (apps/api/src/schedules/schedule.runner.ts, which persists
 * `schedules.last_status`) and every surface that renders it.
 *
 * CSAGE-346: the runner wrote `'succeeded'` while three separate UI
 * `StatusBadge` components tested for `'success'`. Nothing matched, so
 * every healthy subscription fell through to the error branch — the
 * Subscriptions page reported ERROR for runs that had delivered
 * successfully, the red "N errors" badge counted them, the Failing
 * filter returned them, and the `Active` badge was unreachable code.
 *
 * The literals now live here and nowhere else. Producers build values
 * with the helpers below; consumers read them through
 * `classifyScheduleStatus`. Neither side compares raw strings.
 */
/** A run the worker has claimed but not finished. Transient, not a failure. */
export const SCHEDULE_STATUS_RUNNING = 'running';
/** Every delivery channel succeeded. */
export const SCHEDULE_STATUS_SUCCEEDED = 'succeeded';
/** The render succeeded but at least one delivery channel did not. */
export const SCHEDULE_STATUS_PARTIAL = 'partial';
/**
 * Failures persist as `failed:<reason>` — the reason is carried inline
 * rather than in its own column. Use {@link formatScheduleFailure} to
 * build one and {@link scheduleFailureReason} to read it back.
 */
export const SCHEDULE_STATUS_FAILED_PREFIX = 'failed';
/** Longest reason we keep; the column is not unbounded. */
const MAX_FAILURE_REASON = 100;
/**
 * Collapse a persisted `last_status` into a state a UI can switch on.
 *
 * Unrecognized non-empty values classify as `'failed'` rather than
 * silently reading as healthy: a status we don't understand is a status
 * someone should look at. That is deliberately the opposite of the
 * pre-CSAGE-346 behaviour, where anything unrecognized — including the
 * success value — rendered as an error.
 */
export function classifyScheduleStatus(raw) {
    if (raw === null || raw === undefined || raw === '')
        return 'never';
    if (raw === SCHEDULE_STATUS_RUNNING)
        return 'running';
    if (raw === SCHEDULE_STATUS_SUCCEEDED)
        return 'succeeded';
    if (raw === SCHEDULE_STATUS_PARTIAL)
        return 'partial';
    return 'failed';
}
/** Build the value the runner persists for a failed run. */
export function formatScheduleFailure(reason) {
    return `${SCHEDULE_STATUS_FAILED_PREFIX}:${reason.slice(0, MAX_FAILURE_REASON)}`;
}
/**
 * The human-readable reason from a `failed:<reason>` value, or `null`
 * when the status is not a failure or carries no reason. Lets the UI
 * surface *why* a run failed instead of a bare "Error" badge.
 */
export function scheduleFailureReason(raw) {
    if (typeof raw !== 'string')
        return null;
    const prefix = `${SCHEDULE_STATUS_FAILED_PREFIX}:`;
    if (!raw.startsWith(prefix))
        return null;
    const reason = raw.slice(prefix.length).trim();
    return reason.length > 0 ? reason : null;
}
/**
 * True when the last run needs a human to look at it — a hard failure
 * or a partial delivery. This is what "N errors" badges and Failing
 * filters should count.
 *
 * A schedule that has never run is NOT failing, and a run in flight is
 * NOT failing.
 */
export function scheduleNeedsAttention(raw) {
    const state = classifyScheduleStatus(raw);
    return state === 'failed' || state === 'partial';
}
//# sourceMappingURL=schedule-status.js.map